import { OwnerInvite, OwnerInviteInput, toInvite } from './inviteStore';
import { DepositStatus, GeneralInviteTag } from './inviteTags';

const INVITES_URL = '/.netlify/functions/invites';

type InvitesResponse = {
    invites?: Partial<OwnerInvite>[];
    invite?: Partial<OwnerInvite>;
    emailed?: boolean;
    warning?: string;
    created?: number;
    updated?: number;
    skipped?: number;
    error?: string;
};

export type CreateInviteResult = {
    invite: OwnerInvite;
    emailed: boolean;
    warning?: string;
};

export type ImportApplicationsResult = {
    created: number;
    updated: number;
    skipped: number;
};

const functionUnreachableMessage = (): string => {
    const onLocalhost =
        typeof window !== 'undefined' && window.location.hostname === 'localhost';
    return onLocalhost
        ? 'The shared invite list only works on the live site. Local Google sign-in cannot reach the Netlify function.'
        : 'Could not reach the invite function. Confirm Netlify deployed it and FIREBASE_SERVICE_ACCOUNT is set.';
};

const parseResponse = async (
    response: Response,
    fallback: string
): Promise<InvitesResponse> => {
    const text = await response.text();
    let payload: InvitesResponse = {};
    try {
        payload = JSON.parse(text) as InvitesResponse;
    } catch {
        payload = {};
    }

    if (!response.ok && response.status !== 207) {
        if (response.status === 404 || text.trim().startsWith('<')) {
            throw new Error(functionUnreachableMessage());
        }
        throw new Error(payload.error || fallback);
    }
    return payload;
};

const authorizedRequest = async (
    token: string,
    init: RequestInit & { query?: string },
    fallback: string
): Promise<InvitesResponse> => {
    const response = await fetch(`${INVITES_URL}${init.query || ''}`, {
        method: init.method,
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(init.headers || {}),
        },
        body: init.body,
    });
    return parseResponse(response, fallback);
};

const requireInvite = (value: Partial<OwnerInvite> | undefined): OwnerInvite => {
    const invite = toInvite(value || {});
    if (!invite) {
        throw new Error('The server did not return an invite.');
    }
    return invite;
};

export const listRemoteInvites = async (token: string): Promise<OwnerInvite[]> => {
    const payload = await authorizedRequest(token, { method: 'GET' }, 'Could not load invites.');
    return (payload.invites || [])
        .map((item) => toInvite(item))
        .filter((item): item is OwnerInvite => Boolean(item));
};

export const createRemoteInvite = async (
    token: string,
    input: OwnerInviteInput
): Promise<CreateInviteResult> => {
    const payload = await authorizedRequest(
        token,
        {
            method: 'POST',
            body: JSON.stringify(input),
        },
        'Could not save the invite.'
    );
    return {
        invite: requireInvite(payload.invite),
        emailed: Boolean(payload.emailed),
        warning: payload.warning,
    };
};

export const patchRemoteInvite = async (
    token: string,
    update: {
        id: string;
        litter?: string;
        tags?: GeneralInviteTag[];
        depositStatus?: DepositStatus;
        ownerName?: string;
        phone?: string;
        notes?: string;
    }
): Promise<OwnerInvite> => {
    const payload = await authorizedRequest(
        token,
        {
            method: 'PATCH',
            body: JSON.stringify(update),
        },
        'Could not update the invite.'
    );
    return requireInvite(payload.invite);
};

export const importRemoteApplications = async (
    token: string,
    rows: Record<string, string>[]
): Promise<ImportApplicationsResult> => {
    const payload = await authorizedRequest(
        token,
        {
            method: 'POST',
            body: JSON.stringify({ action: 'import-applications', rows }),
        },
        'Could not import the Jotform CSV.'
    );
    return {
        created: payload.created || 0,
        updated: payload.updated || 0,
        skipped: payload.skipped || 0,
    };
};

export const sendRemotePortalInvite = async (token: string, id: string): Promise<OwnerInvite> => {
    const payload = await authorizedRequest(
        token,
        {
            method: 'POST',
            body: JSON.stringify({ action: 'send-portal-invite', id }),
        },
        'Could not email the portal invite.'
    );
    if (!payload.emailed) {
        throw new Error(payload.warning || 'Could not email the portal invite.');
    }
    return requireInvite(payload.invite);
};

export const importRemoteApplication = async (
    token: string,
    input: { id: string; fileName: string; pdfBase64: string }
): Promise<OwnerInvite> => {
    const payload = await authorizedRequest(
        token,
        {
            method: 'PUT',
            body: JSON.stringify(input),
        },
        'Could not read the application PDF.'
    );
    return requireInvite(payload.invite);
};

export const deleteRemoteInvite = async (token: string, id: string): Promise<void> => {
    await authorizedRequest(
        token,
        {
            method: 'DELETE',
            query: `?id=${encodeURIComponent(id)}`,
        },
        'Could not delete the invite.'
    );
};
